import useJobStream from '../hooks/useJobStream';
import LiveJobStatus from '../components/LiveJobStatus';
import React, { useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ExecutionProviderBadge from '../components/ExecutionProviderBadge';
import OpenRecommendationsPanel from '../components/OpenRecommendationsPanel';
import bakingApi from '../services/bakingApi';

function StatusPill({ status }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

function attemptStatusAlert(job, attempt) {
  if (job.status === 'queued') {
    return (
      <Alert variant="info">Waiting for a compatible worker.</Alert>
    );
  }
  if (job.status === 'cancel_requested') {
    return (
      <Alert variant="warning">
        Cancel requested. The worker must stop Unity before the attempt is marked canceled.
      </Alert>
    );
  }
  if (!attempt) return null;
  if (attempt.status === 'claimed') {
    return (
      <Alert variant="warning">
        Assigned to a compatible worker; execution has not started.
      </Alert>
    );
  }
  if (attempt.status === 'preparing') {
    return (
      <Alert variant="info">
        The worker is preparing the controlled Unity fixture.
      </Alert>
    );
  }
  if (attempt.status === 'running') {
    return (
      <Alert variant="primary">Unity is executing the lightmap bake.</Alert>
    );
  }
  if (attempt.status === 'uploading') {
    return (
      <Alert variant="info">The worker is uploading validated bake outputs.</Alert>
    );
  }
  if (attempt.status === 'succeeded' || job.status === 'succeeded') {
    return (
      <Alert variant="success">
        Bake outputs were generated and downloaded. Application occurs inside the Unity Editor
        with user confirmation. Browser Apply is not available.
      </Alert>
    );
  }
  if (attempt.status === 'lost') {
    return (
      <Alert variant="secondary">
        This attempt was lost (worker lease expired). The job may be requeued for another
        attempt.
      </Alert>
    );
  }
  if (attempt.status === 'timed_out') {
    return <Alert variant="danger">Unity execution timed out.</Alert>;
  }
  if (attempt.status === 'canceled' || job.status === 'canceled') {
    return <Alert variant="secondary">Canceled.</Alert>;
  }
  if (job.status === 'failed') {
    return <Alert variant="danger">{job.status_reason}</Alert>;
  }
  return null;
}

function JobDetailPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { revision: liveRevision, status: liveStatus } = useJobStream({ jobId });
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (liveRevision === 0) setLoading(true);
      setError(null);
      try {
        const [jobData, eventData] = await Promise.all([
          bakingApi.getJob(jobId),
          bakingApi.listJobEvents(jobId),
        ]);
        if (!cancelled) {
          setJob(jobData);
          setEvents(eventData);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load job');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, liveRevision]);

  if (loading) {
    return (
      <div className="loading-state">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading job…
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  if (!job) {
    return <div className="empty-state">Job not found.</div>;
  }

  const attempt = job.latest_attempt;
  const warnings = (attempt && attempt.result_warnings) || [];
  const errors = (attempt && attempt.result_errors) || [];

  return (
    <div>
      <p className="mb-2">
        <Link to="/console/jobs">← Jobs</Link>
      </p>
      <h1 className="product-page-title">Job detail</h1>
      <LiveJobStatus status={liveStatus} />
      <p className="mb-3">
        <Link to={`/console/ai?job=${jobId}`} data-testid="ask-ai-job">
          Explain this job with AI Technical Artist
        </Link>
      </p>
      <OpenRecommendationsPanel jobId={jobId} />
      <p className="product-page-subtitle mono">{job.id}</p>

      <div className="product-card">
        <div className="mb-3">
          <StatusPill status={job.status} />
          <ExecutionProviderBadge
            className="ms-2"
            provider={job.execution_provider || 'unity'}
          />
          <span className="ms-2 text-muted">{job.status_reason}</span>
        </div>

        {attemptStatusAlert(job, attempt)}

        {job.revision_is_development_fixture && (
          <Alert variant="secondary" className="mt-2">
            Result label: controlled development fixture execution (not a customer archive).
          </Alert>
        )}

        <dl className="row mb-0">
          <dt className="col-sm-3">Project</dt>
          <dd className="col-sm-9">
            <Link to={`/console/projects/${job.project}`}>{job.project_name}</Link>
          </dd>
          <dt className="col-sm-3">Revision</dt>
          <dd className="col-sm-9 mono">
            {job.revision}
            {job.revision_is_development_fixture && (
              <span className="fixture-badge">development fixture</span>
            )}
          </dd>
          <dt className="col-sm-3">Scene</dt>
          <dd className="col-sm-9 mono">{job.scene_asset_path}</dd>
          <dt className="col-sm-3">Provider</dt>
          <dd className="col-sm-9">
            <ExecutionProviderBadge provider={job.execution_provider || 'unity'} />
          </dd>
          <dt className="col-sm-3">Bake profile</dt>
          <dd className="col-sm-9">{job.bake_profile_name}</dd>
          <dt className="col-sm-3">
            {job.execution_provider === 'maya' ? 'Application' : 'Unity'}
          </dt>
          <dd className="col-sm-9">
            {job.execution_provider === 'maya'
              ? job.application_version
                ? `Maya ${job.application_version} (scene_validation)`
                : 'Maya scene_validation'
              : `${job.unity_version} / ${job.render_pipeline} ${job.render_pipeline_version}`}
          </dd>
          <dt className="col-sm-3">Created</dt>
          <dd className="col-sm-9">{new Date(job.created_at).toLocaleString()}</dd>
          <dt className="col-sm-3">Started</dt>
          <dd className="col-sm-9">
            {job.started_at ? new Date(job.started_at).toLocaleString() : '—'}
          </dd>
          <dt className="col-sm-3">Finished</dt>
          <dd className="col-sm-9">
            {job.finished_at ? new Date(job.finished_at).toLocaleString() : '—'}
          </dd>
          <dt className="col-sm-3">Attempt</dt>
          <dd className="col-sm-9">
            {attempt ? (
              <>
                #{attempt.attempt_number} — <StatusPill status={attempt.status} />
              </>
            ) : (
              'None'
            )}
          </dd>
          <dt className="col-sm-3">Assigned worker</dt>
          <dd className="col-sm-9">
            {job.assigned_worker ? (
              <>
                {job.assigned_worker.name}
                {job.assigned_worker.last_heartbeat_at && (
                  <span className="text-muted ms-2">
                    last heartbeat{' '}
                    {new Date(job.assigned_worker.last_heartbeat_at).toLocaleString()}
                  </span>
                )}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt className="col-sm-3">Lease</dt>
          <dd className="col-sm-9">
            {attempt && attempt.active_lease ? (
              <>
                expires {new Date(attempt.active_lease.expires_at).toLocaleString()}
                {attempt.active_lease.is_expired ? ' (expired)' : ''}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt className="col-sm-3">Editor exit code</dt>
          <dd className="col-sm-9">
            {attempt && attempt.exit_code !== null && attempt.exit_code !== undefined
              ? attempt.exit_code
              : '—'}
          </dd>
          <dt className="col-sm-3">Editor log</dt>
          <dd className="col-sm-9">
            {attempt && attempt.editor_log_captured_locally
              ? 'Captured locally on the worker (not downloadable)'
              : '—'}
          </dd>
          <dt className="col-sm-3">Process started</dt>
          <dd className="col-sm-9">
            {attempt && attempt.process_started_at
              ? new Date(attempt.process_started_at).toLocaleString()
              : '—'}
          </dd>
          <dt className="col-sm-3">Process finished</dt>
          <dd className="col-sm-9">
            {attempt && attempt.process_finished_at
              ? new Date(attempt.process_finished_at).toLocaleString()
              : '—'}
          </dd>
        </dl>

        {(warnings.length > 0 || errors.length > 0) && (
          <div className="mt-3">
            {warnings.length > 0 && (
              <>
                <h3 className="h6">Structured warnings</h3>
                <ul>
                  {warnings.map((w, i) => (
                    <li key={`w-${i}`}>{String(w)}</li>
                  ))}
                </ul>
              </>
            )}
            {errors.length > 0 && (
              <>
                <h3 className="h6">Structured errors</h3>
                <ul>
                  {errors.map((e, i) => (
                    <li key={`e-${i}`}>{String(e)}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      <div className="product-card">
        <h2 className="h5 mb-3">Artifacts</h2>
        {!(job.artifacts && job.artifacts.length) ? (
          <p className="text-muted mb-0">No artifacts yet.</p>
        ) : (
          <ul className="event-list">
            {job.artifacts.map((artifact) => (
              <li key={artifact.id}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{artifact.artifact_type}</strong>{' '}
                    <StatusPill status={artifact.status} />
                    <div className="text-muted">
                      {artifact.original_filename} · {artifact.byte_size} bytes
                      {artifact.sha256 ? ` · ${artifact.sha256.slice(0, 12)}…` : ''}
                      {artifact.finalized_at
                        ? ` · finalized ${new Date(artifact.finalized_at).toLocaleString()}`
                        : ''}
                    </div>
                  </div>
                  {artifact.is_downloadable && artifact.status === 'validated' ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={async () => {
                        try {
                          const auth = await bakingApi.createArtifactDownloadAuth(artifact.id);
                          window.open(auth.download_url, '_blank', 'noopener,noreferrer');
                        } catch (err) {
                          setError(err.message || 'Download authorization failed');
                        }
                      }}
                    >
                      Download
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Alert variant="light" className="mt-3 mb-0 border">
          Bake outputs were generated and downloaded. Application occurs inside the Unity Editor
          with user confirmation. Browser Apply is not available.
        </Alert>
      </div>

      <div className="product-card">
        <h2 className="h5 mb-3">Events</h2>
        {events.length === 0 ? (
          <p className="text-muted mb-0">No events.</p>
        ) : (
          <ul className="event-list">
            {events.map((event) => (
              <li key={event.id}>
                <div className="d-flex justify-content-between">
                  <strong>{event.event_type}</strong>
                  <span className="text-muted">
                    {new Date(event.created_at).toLocaleString()}
                  </span>
                </div>
                <div>{event.message}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default JobDetailPage;
